import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useSidebarContext } from '../contexts/SidebarContext';
import { useTranslation } from '../i18n';
import { shares } from '../api/index';
import useDialog from '../hooks/useDialog';
import './SharingPanel.scss';

/**
 * SharingPanel - unified panel for sharing projects and viewing shared projects
 * Combines ShareModal and SharedWithMePanel functionality
 */
function SharingPanel({ isOpen, onClose }) {
  const { projects, currentCalendar } = useCalendarDataContext();
  const { sidebarOpen } = useSidebarContext();
  const { t } = useTranslation();
  const { confirm } = useDialog();
  const panelRef = useRef(null);

  // Close sharing panel when sidebar opens (e.g., clicking an event)
  useEffect(() => {
    if (isOpen && sidebarOpen) {
      onClose();
    }
  }, [isOpen, sidebarOpen, onClose]);

  // Active tab: 'share' or 'shared-with-me'
  const [activeTab, setActiveTab] = useState('share');
  
  // Share tab state
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [projectSelections, setProjectSelections] = useState({});
  const [existingShares, setExistingShares] = useState({});
  const [recentContacts, setRecentContacts] = useState([]);
  
  // Shared with me tab state
  const [sharedProjects, setSharedProjects] = useState([]);
  
  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const searchWrapperRef = useRef(null);

  // Filter to only owned projects - memoized to prevent infinite loop
  const ownedProjects = useMemo(() => 
    projects.filter(p => !p.isIncluded && !p.isSystem),
    [projects]
  );

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset state when panel opens
  useEffect(() => {
    if (!isOpen) return;

    const initial = {};
    ownedProjects.forEach(p => {
      initial[p.id] = { selected: false, permission: 'read' };
    });
    setProjectSelections(initial);
    setEmail('');
    setSearchResults([]);
    setError(null);
  }, [isOpen, ownedProjects]);

  // Load data when panel opens
  useEffect(() => {
    if (!isOpen) return;

    async function loadData() {
      setIsLoading(true);
      setError(null);
      
      try {
        // Load recent contacts
        const contacts = await shares.getRecentContacts();
        setRecentContacts(contacts);

        // Load existing shares for owned projects
        const sharesMap = {};
        for (const project of ownedProjects) {
          const projectShares = await shares.listProjectShares(project.id);
          sharesMap[project.id] = projectShares;
        }
        setExistingShares(sharesMap);

        // Load shared with me projects
        const shared = await shares.getSharedWithMe();
        setSharedProjects(shared);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [isOpen, ownedProjects]);

  // Search users when email changes
  useEffect(() => {
    if (email.length < 3) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await shares.searchUsers(email);
        setSearchResults(results);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [email]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    }
    
    if (searchResults.length > 0) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [searchResults.length]);

  // Share tab handlers
  const handleProjectToggle = useCallback((projectId) => {
    setProjectSelections(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        selected: !prev[projectId]?.selected,
      },
    }));
  }, []);

  const handlePermissionChange = useCallback((projectId, permission) => {
    setProjectSelections(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        permission,
      },
    }));
  }, []);

  const handleSelectUser = (user) => {
    setEmail(user.email);
    setSearchResults([]);
  };

  const handleShare = async () => {
    const selectedProjects = Object.entries(projectSelections)
      .filter(([, value]) => value.selected)
      .map(([projectId, value]) => ({ projectId, permission: value.permission }));

    if (selectedProjects.length === 0) {
      setError(t('sharing.selectProjectsError'));
      return;
    }

    if (!email.includes('@')) {
      setError(t('sharing.invalidEmail'));
      return;
    }

    // Check if any selected project is already shared with this email
    const normalizedEmail = email.trim().toLowerCase();
    const alreadySharedProjects = selectedProjects.filter(({ projectId }) => {
      const projectShareList = existingShares[projectId] || [];
      return projectShareList.some(s => s.email?.toLowerCase() === normalizedEmail);
    });

    if (alreadySharedProjects.length > 0) {
      const projectNames = alreadySharedProjects
        .map(({ projectId }) => ownedProjects.find(p => p.id === projectId)?.name)
        .filter(Boolean)
        .join(', ');
      setError(t('sharing.alreadyShared', { projects: projectNames, email }));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      for (const { projectId, permission } of selectedProjects) {
        const newShare = await shares.shareProject(projectId, { email, permission });
        setExistingShares(prev => ({
          ...prev,
          [projectId]: [...(prev[projectId] || []), newShare],
        }));
      }

      // Reset form
      setEmail('');
      const resetSelections = {};
      ownedProjects.forEach(p => {
        resetSelections[p.id] = { selected: false, permission: 'read' };
      });
      setProjectSelections(resetSelections);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveShare = async (projectId, share) => {
    const confirmed = await confirm({
      title: t('sharing.removeAccess'),
      message: t('sharing.removeAccessConfirm', { name: share.name || share.email }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await shares.deleteShare(share.id);
      } catch (err) {
        if (!err.message.toLowerCase().includes('not found')) {
          setError(err.message);
          return;
        }
      }
      setExistingShares(prev => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter(s => s.id !== share.id),
      }));
    }
  };

  // Shared with me tab handlers
  const handleInclude = useCallback(async (projectId) => {
    if (!currentCalendar) return;

    try {
      const result = await shares.includeProject(currentCalendar.id, projectId);
      setSharedProjects(prev =>
        prev.map(p => p.id === projectId
          ? { ...p, isIncluded: true, includedInCalendarId: currentCalendar.id, includedId: result.id }
          : p
        )
      );
      // WebSocket will handle adding the project/events/milestones automatically
    } catch (err) {
      setError(err.message);
    }
  }, [currentCalendar]);

  const handleRemoveIncluded = useCallback(async (includedId, projectId) => {
    try {
      await shares.removeIncluded(includedId);
      setSharedProjects(prev =>
        prev.map(p => p.id === projectId
          ? { ...p, isIncluded: false, includedInCalendarId: null, includedId: null }
          : p
        )
      );
      // WebSocket will handle removing the project/events/milestones automatically
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Compute existing shares grouped by user
  const allShares = new Map();
  Object.entries(existingShares).forEach(([projectId, projectShareList]) => {
    const project = ownedProjects.find(p => p.id === projectId);
    if (!project) return;

    (projectShareList || []).forEach(share => {
      const key = share.userId;
      if (!allShares.has(key)) {
        allShares.set(key, {
          user: {
            id: share.userId,
            email: share.email,
            name: share.name,
            avatarUrl: share.avatarUrl,
          },
          projects: [],
        });
      }
      allShares.get(key).projects.push({
        projectId,
        projectName: project.name,
        projectColor: project.color,
        shareId: share.id,
        permission: share.permission,
      });
    });
  });

  const selectedCount = Object.values(projectSelections).filter(v => v.selected).length;
  const pendingSharesCount = sharedProjects.filter(p => !p.isIncluded).length;

  if (!isOpen) return null;

  return (
    <div className="sharing-panel" ref={panelRef}>
      <div className="sharing-panel__header">
        <h3 className="sharing-panel__title">{t('sharing.title')}</h3>
        <button
          className="sharing-panel__close"
          onClick={onClose}
          type="button"
          aria-label={t('common.close')}
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <div className="sharing-panel__tabs">
        <button
          className={`sharing-panel__tab ${activeTab === 'share' ? 'sharing-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('share')}
        >
          {t('sharing.myShares')}
        </button>
        <button
          className={`sharing-panel__tab ${activeTab === 'shared-with-me' ? 'sharing-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('shared-with-me')}
        >
          {t('sharing.sharedWithMe')}
          {pendingSharesCount > 0 && (
            <span className="sharing-panel__tab-badge">{pendingSharesCount}</span>
          )}
        </button>
      </div>

      <div className="sharing-panel__content">
        {isLoading ? (
          <div className="sharing-panel__loading">{t('common.loading')}</div>
        ) : activeTab === 'share' ? (
          /* Share Tab */
          <div className="sharing-panel__share-tab">
            {/* Existing shares */}
            <div className="sharing-panel__section">
              <span className="sharing-panel__section-label">{t('sharing.sharedWith')}</span>
              {allShares.size === 0 ? (
                <div className="sharing-panel__empty">{t('sharing.notShared')}</div>
              ) : (
                <div className="sharing-panel__share-list">
                  {[...allShares.values()].map(({ user, projects: sharedProjects }) => (
                    <div key={user.id} className="sharing-panel__share-user">
                      <div className="sharing-panel__share-user-header">
                        <div className="sharing-panel__avatar sharing-panel__avatar--placeholder">
                          {(user.name || user.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="sharing-panel__share-info">
                          <span className="sharing-panel__share-name">{user.name || user.email}</span>
                          {user.name && <span className="sharing-panel__share-email">{user.email}</span>}
                        </div>
                      </div>
                      <div className="sharing-panel__share-projects">
                        {sharedProjects.map(({ projectId, projectName, projectColor, shareId, permission }) => (
                          <div key={shareId} className="sharing-panel__share-project">
                            <span
                              className="sharing-panel__project-color"
                              style={{ backgroundColor: projectColor }}
                            />
                            <span className="sharing-panel__project-name">{projectName}</span>
                            <span className="sharing-panel__permission">{permission === 'write' ? t('sharing.canEdit') : t('sharing.canView')}</span>
                            <button
                              className="sharing-panel__remove-btn"
                              onClick={() => handleRemoveShare(projectId, { ...user, id: shareId })}
                              title={t('sharing.removeAccess')}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add new share */}
            <div className="sharing-panel__divider">
              <span>{t('sharing.addNew')}</span>
            </div>

            {/* Project selection */}
            <div className="sharing-panel__section">
              <span className="sharing-panel__section-label">{t('sharing.selectProjects')}</span>
              {ownedProjects.length === 0 ? (
                <div className="sharing-panel__empty">{t('sharing.noProjects')}</div>
              ) : (
                <div className="sharing-panel__project-list">
                  {ownedProjects.map((project) => (
                    <div key={project.id} className="sharing-panel__project-item">
                      <label className="sharing-panel__project-checkbox">
                        <input
                          type="checkbox"
                          checked={projectSelections[project.id]?.selected || false}
                          onChange={() => handleProjectToggle(project.id)}
                        />
                        <span
                          className="sharing-panel__project-color"
                          style={{ backgroundColor: project.color }}
                        />
                        <span className="sharing-panel__project-name">{project.name}</span>
                      </label>
                      {projectSelections[project.id]?.selected && (
                        <select
                          className="sharing-panel__permission-select"
                          value={projectSelections[project.id]?.permission || 'read'}
                          onChange={(e) => handlePermissionChange(project.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="read">{t('sharing.canView')}</option>
                          <option value="write">{t('sharing.canEdit')}</option>
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email input */}
            <div className="sharing-panel__section" ref={searchWrapperRef}>
              {recentContacts.length > 0 && !email && (
                <div className="sharing-panel__recent">
                  <span className="sharing-panel__section-label">{t('sharing.recentContacts')}</span>
                  <div className="sharing-panel__contacts">
                    {recentContacts.map((contact) => (
                      <button
                        key={contact.id}
                        className="sharing-panel__contact"
                        onClick={() => setEmail(contact.email)}
                      >
                        <div className="sharing-panel__avatar sharing-panel__avatar--small sharing-panel__avatar--placeholder">
                          {(contact.name || contact.email).charAt(0).toUpperCase()}
                        </div>
                        <span>{contact.name || contact.email.split('@')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="sharing-panel__input-row">
                <div className="sharing-panel__input-wrapper">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setTimeout(() => setIsInputFocused(false), 150)}
                    placeholder={t('sharing.emailPlaceholder')}
                    className="sharing-panel__email-input"
                  />
                  {isSearching && (
                    <span className="sharing-panel__searching">{t('sharing.searching')}</span>
                  )}
                  {searchResults.length > 0 && isInputFocused && (
                    <div className="sharing-panel__search-results">
                      {searchResults.map((user) => (
                        <button
                          key={user.id}
                          className="sharing-panel__search-result"
                          onClick={() => handleSelectUser(user)}
                        >
                          <div className="sharing-panel__avatar sharing-panel__avatar--placeholder">
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="sharing-panel__user-info">
                            <span className="sharing-panel__user-name">{user.name || user.email}</span>
                            {user.name && <span className="sharing-panel__user-email">{user.email}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="sharing-panel__share-btn"
                  onClick={handleShare}
                  disabled={!email.includes('@') || selectedCount === 0 || isLoading}
                >
                  {t('sharing.share')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Shared with me Tab */
          <div className="sharing-panel__shared-tab">
            {sharedProjects.length === 0 ? (
              <div className="sharing-panel__empty">{t('sharing.noSharedProjects')}</div>
            ) : (
              <div className="sharing-panel__shared-list">
                {sharedProjects.map(project => (
                  <div key={project.id} className="sharing-panel__shared-project">
                    <div className="sharing-panel__shared-project-header">
                      <span
                        className="sharing-panel__project-color"
                        style={{ backgroundColor: project.color }}
                      />
                      <div className="sharing-panel__shared-project-info">
                        <span className="sharing-panel__project-name">{project.name}</span>
                        {project.owner && (
                          <span className="sharing-panel__project-owner">
                            {project.owner.name || project.owner.email}
                          </span>
                        )}
                      </div>
                      <div className="sharing-panel__shared-project-actions">
                        {project.isIncluded ? (
                          <button
                            className="sharing-panel__action-btn sharing-panel__action-btn--remove"
                            onClick={() => handleRemoveIncluded(project.includedId, project.id)}
                          >
                            {t('sharing.removeFromCalendar')}
                          </button>
                        ) : (
                          <button
                            className="sharing-panel__action-btn sharing-panel__action-btn--include"
                            onClick={() => handleInclude(project.id)}
                          >
                            {t('sharing.includeInCalendar')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="sharing-panel__error">{error}</div>
        )}
      </div>
    </div>
  );
}

export default SharingPanel;
